import type { CurrencyCode } from '@noths/polaris-client-localisation';

import type { CatServiceAPIProductOption } from 'src/services/cat-service-api/types/CatServiceAPIProductOption';
import { getOptionAmount } from 'src/utils/price';

const getHighestCostOptionValue = (option: CatServiceAPIProductOption, currency: CurrencyCode) => {
  const price = option.price ?? 0;

  return (option.values || []).reduce(
    (highest, value) =>
      Math.max(getOptionAmount(currency, value.priceModifier).value + price, highest),
    price,
  );
};

const getLowestCostOptionValue = (option: CatServiceAPIProductOption, currency: CurrencyCode) => {
  const price = option.price ?? 0;

  if (!option.values?.some((value) => value.priceModifier !== null)) {
    return price;
  }

  return (option.values || []).reduce(
    (lowest, value) =>
      Math.min(getOptionAmount(currency, value.priceModifier).value + price, lowest),
    Infinity,
  );
};

interface Range {
  maxOptional: number;
  maxRequired: number;
  minOptional: number;
  minRequired: number;
}

export const getOptionsCostRange = (
  options: CatServiceAPIProductOption[],
  currency: CurrencyCode,
): Range => {
  const initialRange: Range = {
    maxOptional: 0,
    maxRequired: 0,
    minOptional: 0,
    minRequired: 0,
  };
  const optionsWithPriceModifiers = options.filter((option) => {
    if (option.price != null) {
      return true;
    }

    if (option.values === null) {
      return false;
    }

    return option.values.some((value) => value.priceModifier !== null);
  });

  if (optionsWithPriceModifiers.length === 0) {
    return initialRange;
  }

  return optionsWithPriceModifiers.reduce((range, option) => {
    const highestCostOptionValue = getHighestCostOptionValue(option, currency);
    const lowestCostOptionValue = getLowestCostOptionValue(option, currency);

    return option.required
      ? {
          ...range,
          maxRequired: range.maxRequired + highestCostOptionValue,
          minRequired: range.minRequired + lowestCostOptionValue,
        }
      : {
          ...range,
          maxOptional: range.maxOptional + highestCostOptionValue,
          minOptional: range.minOptional + lowestCostOptionValue,
        };
  }, initialRange);
};
